import React, { useEffect, useState, useRef } from "react";
import ilodrop from "../../assets/icons/ilodrop.svg";
import { fetchPublisherYearSuggestions, fetchPublisherSuggestions, fetchPublisherVolumeSuggestions, fetchPublisherPageSuggestions } from "../../service/byPublisher"
import { fetchNeutralCourtSuggestions, fetchNeutralYearSuggestions, fetchNeutralAbbrSuggestions, fetchNeutralPageSuggestions } from "../../service/byCitation"
import "./Citation.scss"

function Citation({ onChange }) {
    const [activeCitation, setAciveCitation] = useState("By Publisher");

    // By Publisher fields
    const [pubYear, setPubYear] = useState("");
    const [yearSuggestions, setYearSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFromCourtDropdown, setSelectedFromCourtDropdown] = useState(false);
    const yearRef = useRef(null);

    const [publisherValue, setPublisherValue] = useState("");
    const [publisherSuggestions, setPublisherSuggestions] = useState([]);
    const [publisherLoading, setPublisherLoading] = useState(false);
    const [selectedPublisher, setSelectedPublisher] = useState(false);
    const publisherRef = useRef(null);

    const [volumeValue, setVolumeValue] = useState("");
    const [volumeSuggestions, setVolumeSuggestions] = useState([]);
    const [volumeLoading, setVolumeLoading] = useState(false);
    const [selectedVolume, setSelectedVolume] = useState(false);
    const volumeRef = useRef(null);

    const [pageValue, setPageValue] = useState("");
    const [pageSuggestions, setPageSuggestions] = useState([]);
    const [pageLoading, setPageLoading] = useState(false);
    const [selectedPage, setSelectedPage] = useState(false);
    const pageRef = useRef(null);

    // By Neutral Citation fields
    const [ncYear, setNcYear] = useState(""); // To be sent
    const [courtName, setCourtName] = useState(""); // To be sent
    const [ncAbbr, setNcAbbr] = useState("");
    const [ncPageNo, setNcPageNo] = useState("");

    const [courtSuggestions, setCourtSuggestions] = useState([]);
    const [isLoadingCourt, setIsLoadingCourt] = useState(false);
    const [showCourtDropdown, setShowCourtDropdown] = useState(false);
    const courtRef = useRef(null);

    const [selectedFromDropdown, setSelectedFromDropdown] = useState(false);

    const [isLoadingYear, setIsLoadingYear] = useState(false);
    const [showYearDropdown, setShowYearDropdown] = useState(false);
    const [selectedFromCitationYearDropdown, setSelectedFromCitationYearDropdown] = useState(false);
    const ncyearRef = useRef(null);

    const [abbrSuggestions, setAbbrSuggestions] = useState([]);
    const [isLoadingAbbr, setIsLoadingAbbr] = useState(false);
    const [showAbbrDropdown, setShowAbbrDropdown] = useState(false);
    const [selectedFromAbbrDropdown, setSelectedFromAbbrDropdown] = useState(false);
    const [citationAbbr, setCitationAbbr] = useState(""); // To be sent
    const abbrRef = useRef(null);

    const [citationPageSuggestions, setCitationPageSuggestions] = useState([]);
    const [isLoadingCitationPage, setIsLoadingCitationPage] = useState(false);
    const [showCitationPageDropdown, setShowCitationPageDropdown] = useState(false);
    const [citationPage, setCitationPage] = useState(""); // To be sent
    const [selectedFromCitationPageDropdown, setSelectedFromCitationPageDropdown] = useState(false);
    const citationPageRef = useRef(null);

    useEffect(() => {
        if (typeof onChange === "function") {
            if (activeCitation === "By Publisher") {
                onChange({
                    activeCitation,
                    pubYear: pubYear,
                    publisherValue,
                    volumeValue,
                    pageValue,
                });
            } else {
                onChange({
                    activeCitation,
                    ncYear: ncYear,
                    courtName: courtName,
                    citationAbbr: citationAbbr,
                    citationPage: citationPage
                });
            }
        }
    }, [
        activeCitation,
        pubYear,
        publisherValue,
        volumeValue,
        pageValue,
        ncYear,
        courtName,
        citationAbbr,
        citationPage,
        onChange,
    ]);

    useEffect(() => {
        if (
            activeCitation !== "By Publisher" ||
            selectedFromDropdown ||
            !pubYear.trim()
        ) {
            setYearSuggestions([]); // close dropdown if input is empty
            setIsLoading(false);
            setYearSuggestions(false)
            return;
        }

        const controller = new AbortController();
        const signal = controller.signal;

        const fetchSuggestions = async () => {
            try {
                setIsLoading(true);
                const data = await fetchPublisherYearSuggestions(pubYear, publisherValue);
                setYearSuggestions(data || []);
            } catch (err) {
                console.error("Error fetching year suggestions:", err);
                setYearSuggestions([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSuggestions();

        return () => controller.abort();
    }, [pubYear, publisherValue, activeCitation, selectedFromDropdown]);

    const handleInputChange = (value) => {
        setPubYear(value);
        setSelectedFromDropdown(false); // reset if user types again
    };

    const handleSelectSuggestion = (value) => {
        setPubYear(value);
        setSelectedFromDropdown(true); // prevent further API calls for selected value
        setYearSuggestions([]); // close dropdown
    };

    // Handle outside clicks
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (yearRef.current && !yearRef.current.contains(event.target)) {
                setYearSuggestions([]);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch publisher suggestions
    useEffect(() => {
        if (!publisherValue.trim() || selectedPublisher) {
            setPublisherSuggestions([]);
            setPublisherLoading(false);
            return;
        }

        const controller = new AbortController();
        const signal = controller.signal;

        const fetchData = async () => {
            try {
                setPublisherLoading(true);
                const data = await fetchPublisherSuggestions(publisherValue, pubYear);
                setPublisherSuggestions(data || []);
            } catch (err) {
                console.error("Error fetching publisher suggestions:", err);
                setPublisherSuggestions([]);
            } finally {
                setPublisherLoading(false);
            }
        };

        fetchData();
        return () => controller.abort();
    }, [publisherValue, pubYear, selectedPublisher]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (publisherRef.current && !publisherRef.current.contains(event.target)) {
                setPublisherSuggestions([]);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelectPublisher = (value) => {
        setPublisherValue(value);
        setSelectedPublisher(true);
        setPublisherSuggestions([]);
    };

    const handlePublisherChange = (value) => {
        setPublisherValue(value);
        setSelectedPublisher(false);
        if (!value.trim()) setPublisherSuggestions([]);
    };

    // Fetch suggestions (on focus or typing)
    const getVolumeSuggestions = async (prefix = "") => {
        if (!pubYear || !publisherValue) return;
        try {
            setVolumeLoading(true);
            const data = await fetchPublisherVolumeSuggestions(prefix, pubYear, publisherValue);
            setVolumeSuggestions(data || []);
        } catch (error) {
            console.error("Error fetching volume suggestions:", error);
            setVolumeSuggestions([]);
        } finally {
            setVolumeLoading(false);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (volumeRef.current && !volumeRef.current.contains(event.target)) {
                setVolumeSuggestions([]);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handle input changes
    const handleVolumeChange = (value) => {
        setVolumeValue(value);
        setSelectedVolume(false);
        if (value.trim()) {
            getVolumeSuggestions(value);
        } else {
            setVolumeSuggestions([]);
        }
    };

    const handleSelectVolume = (value) => {
        setVolumeValue(value);
        setSelectedVolume(true);
        setVolumeSuggestions([]);
    };

    const getPageSuggestions = async (prefix = "") => {
        if (!pubYear || !publisherValue || !volumeValue) return;
        try {
            setPageLoading(true);
            const data = await fetchPublisherPageSuggestions(
                prefix,
                pubYear,
                publisherValue,
                volumeValue
            );
            setPageSuggestions(data || []);
        } catch (error) {
            console.error("Error fetching page suggestions:", error);
            setPageSuggestions([]);
        } finally {
            setPageLoading(false);
        }
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pageRef.current && !pageRef.current.contains(event.target)) {
                setPageSuggestions([]);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handle input change
    const handlePageChange = (value) => {
        setPageValue(value);
        setSelectedPage(false);
        if (value.trim()) {
            getPageSuggestions(value);
        } else {
            setPageSuggestions([]);
        }
    };

    const handleSelectPage = (value) => {
        setPageValue(value);
        setSelectedPage(true);
        setPageSuggestions([]);
    };

    useEffect(() => {

        if (selectedFromCourtDropdown) return;

        const fetchSuggestions = async () => {
            if (!courtName.trim()) {
                setCourtSuggestions([]);
                setShowCourtDropdown(false);
                return;
            }

            setIsLoadingCourt(true);
            try {
                const data = await fetchNeutralCourtSuggestions(courtName, ncYear);
                setCourtSuggestions(data);
                setShowCourtDropdown(true);
            } catch (err) {
                console.error("Error fetching court suggestions:", err);
            } finally {
                setIsLoadingCourt(false);
            }
        };

        const delayDebounce = setTimeout(fetchSuggestions, 300); // debounce for smoother typing
        return () => clearTimeout(delayDebounce);
    }, [courtName]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (courtRef.current && !courtRef.current.contains(e.target)) {
                setShowCourtDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelectCourt = (value) => {
        setCourtName(value);
        setShowCourtDropdown(false);
        setSelectedFromCourtDropdown(true)
    };

    const handleCourtChange = (e) => {
        setCourtName(e.target.value);
        setSelectedFromCourtDropdown(false);
    };

    // Citation Year
    useEffect(() => {
        if (selectedFromCitationYearDropdown) return; // skip API if user selected a value

        const fetchSuggestions = async () => {
            if (!ncYear.trim()) {
                setYearSuggestions([]);
                setShowYearDropdown(false);
                return;
            }

            setIsLoadingYear(true);
            try {
                const data = await fetchNeutralYearSuggestions(ncYear);
                setYearSuggestions(data);
                setShowYearDropdown(true);
            } catch (err) {
                console.error("Error fetching year suggestions:", err);
            } finally {
                setIsLoadingYear(false);
            }
        };

        const debounce = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(debounce);
    }, [ncYear, selectedFromDropdown]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (yearRef.current && !yearRef.current.contains(e.target)) {
                setShowYearDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelectYear = (value) => {
        setNcYear(value);
        setSelectedFromCitationYearDropdown(true); // mark as selected
        setShowYearDropdown(false);
    };

    const handleCitationYearChange = (e) => {
        setNcYear(e.target.value);
        setSelectedFromCitationYearDropdown(false); // allow API call if user edits
    };

    // Citation NC-Abbr
    useEffect(() => {
        if (selectedFromAbbrDropdown) return; // skip API if user selected

        const fetchSuggestions = async () => {
            if (!citationAbbr.trim()) {
                setAbbrSuggestions([]);
                setShowAbbrDropdown(false);
                return;
            }

            setIsLoadingAbbr(true);
            try {
                const data = await fetchNeutralAbbrSuggestions(citationAbbr, ncYear, courtName);
                setAbbrSuggestions(data);
                setShowAbbrDropdown(true);
            } catch (err) {
                console.error("Error fetching CitationAbbr suggestions:", err);
            } finally {
                setIsLoadingAbbr(false);
            }
        };

        const debounce = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(debounce);
    }, [citationAbbr, ncYear, courtName, selectedFromDropdown]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (abbrRef.current && !abbrRef.current.contains(e.target)) {
                setShowAbbrDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelectAbbr = (value) => {
        setCitationAbbr(value);
        setSelectedFromAbbrDropdown(true);
        setShowAbbrDropdown(false);
    };

    const handleChange = (e) => {
        setCitationAbbr(e.target.value);
        setSelectedFromAbbrDropdown(false);
    };

    // Citation Page
    useEffect(() => {
        if (selectedFromDropdown) return;

        const fetchSuggestions = async () => {
            if (!citationPage.trim()) {
                setCitationPageSuggestions([]);
                setShowCitationPageDropdown(false);
                return;
            }

            setIsLoadingCitationPage(true);
            try {
                const data = await fetchNeutralPageSuggestions(citationPage, ncYear, courtName, citationAbbr);
                setCitationPageSuggestions(data);
                setShowCitationPageDropdown(true);
            } catch (err) {
                console.error("Error fetching citation page suggestions:", err);
            } finally {
                setIsLoadingCitationPage(false);
            }
        };

        const debounce = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(debounce);
    }, [citationPage, ncYear, courtName, citationAbbr, selectedFromDropdown]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (citationPageRef.current && !citationPageRef.current.contains(e.target)) {
                setShowCitationPageDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelectCitationPage = (value) => {
        setCitationPage(value);
        setSelectedFromDropdown(true);
        setShowCitationPageDropdown(false);
    };

    const handleCitationPageChange = (e) => {
        setCitationPage(e.target.value);
        setSelectedFromDropdown(false); // allow API call if user edits
    };

    return (
        <div className="landing-page-citation-main">
            <div className="landing-page-citation">
                <div className="landing-page-citation-bar">
                    <div
                        onClick={() => setAciveCitation("By Publisher")}
                        className={`landing-page-citation-bar-tab ${activeCitation === "By Publisher" ? "landing-page-citation-bar-tab-active" : ""}`}
                    >
                        By Publisher
                    </div>

                    <div
                        onClick={() => setAciveCitation("By Neutral Citation")}
                        className={`landing-page-citation-bar-tab ${activeCitation === "By Neutral Citation" ? "landing-page-citation-bar-tab-active" : ""}`}
                    >
                        By Neutral Citation
                    </div>
                </div>
            </div>
            {
                activeCitation === "By Publisher" && (
                    <div className="landing-page-citation-bar-tab-field-container" style={{ position: "relative" }}>
                        <div
                            ref={publisherRef}
                            style={{ position: "relative", width: "64%" }}
                            className="landing-page-citation-bar-tab-field"
                        >
                            <input
                                placeholder="Publisher"
                                value={publisherValue}
                                onChange={(e) => handlePublisherChange(e.target.value)}
                                autoComplete="off"
                            />

                            {/* Loading */}
                            {publisherValue && publisherLoading && (
                                <div style={{ width: "40%" }} className="landing-page-citation-suggestions-dropdown-loading">Loading...</div>
                            )}

                            {/* Dropdown */}
                            {!publisherLoading && publisherSuggestions.length > 0 && (
                                <div style={{ width: "40%" }} className="landing-page-citation-suggestions-dropdown">
                                    {publisherSuggestions.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="landing-page-citation-suggestions-dropdown-item"
                                            onMouseDown={() => handleSelectPublisher(item)}
                                        >
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div style={{ width: "12%" }} className="landing-page-citation-bar-tab-field">
                            <input
                                placeholder="Year"
                                value={pubYear}
                                onChange={(e) => handleInputChange(e.target.value)}
                                autoComplete="off"
                            />

                            {/* Loading */}
                            {pubYear && isLoading && (
                                <div className="landing-page-citation-suggestions-dropdown-loading">Loading...</div>
                            )}

                            {/* Dropdown suggestions */}
                            {!isLoading && yearSuggestions.length > 0 && (
                                <div ref={yearRef} className="landing-page-citation-suggestions-dropdown">
                                    {yearSuggestions.map((year, idx) => (
                                        <div
                                            key={idx}
                                            className="landing-page-citation-suggestions-dropdown-item"
                                            onMouseDown={() => handleSelectSuggestion(year)}
                                        >
                                            {year}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div
                            ref={volumeRef}
                            style={{ position: "relative", width: "12%" }}
                            className="landing-page-citation-bar-tab-field"
                        >
                            <input
                                placeholder="Vol No."
                                value={volumeValue}
                                onChange={(e) => handleVolumeChange(e.target.value)}
                                onFocus={() => getVolumeSuggestions("")} // trigger API on focus
                                autoComplete="off"
                            />

                            {/* Loading */}
                            {volumeLoading && <div className="landing-page-citation-suggestions-dropdown-loading">Loading...</div>}

                            {/* Suggestions Dropdown */}
                            {!volumeLoading && volumeSuggestions.length > 0 && (
                                <div className="landing-page-citation-suggestions-dropdown">
                                    {volumeSuggestions.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="landing-page-citation-suggestions-dropdown-item"
                                            onMouseDown={() => handleSelectVolume(item)}
                                        >
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div
                            ref={pageRef}
                            style={{ position: "relative", width: "12%" }}
                            className="landing-page-citation-bar-tab-field"
                        >
                            <input
                                placeholder="Page No."
                                value={pageValue}
                                onChange={(e) => handlePageChange(e.target.value)}
                                // onFocus={() => getPageSuggestions("")}
                                autoComplete="off"
                            />

                            {/* Loading */}
                            {pageLoading && <div className="landing-page-citation-suggestions-dropdown-loading">Loading...</div>}

                            {/* Dropdown */}
                            {!pageLoading && pageSuggestions.length > 0 && (
                                <div className="landing-page-citation-suggestions-dropdown">
                                    {pageSuggestions.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="landing-page-citation-suggestions-dropdown-item"
                                            onMouseDown={() => handleSelectPage(item)}
                                        >
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )
            }
            {
                activeCitation === "By Neutral Citation" && (
                    <div className="landing-page-citation-bar-tab-field-container">
                        <div
                            style={{ width: "64%", position: "relative" }}
                            className="landing-page-citation-bar-tab-field"
                            ref={courtRef}
                        >
                            <input
                                placeholder="Court Name"
                                value={courtName}
                                onChange={handleCourtChange}
                                onFocus={() => {
                                    // if (courtSuggestions.length > 0) setShowCourtDropdown(true);
                                }}
                            />
                            {/* Suggestions Dropdown */}
                            {showCourtDropdown && (
                                <div style={{ top: "40px", left: "0px" }} className="landing-page-suggestions-dropdown">
                                    {isLoadingCourt ? (
                                        <div className="landing-page-suggestions-loading">Loading...</div>
                                    ) : courtSuggestions.length > 0 ? (
                                        courtSuggestions.map((item, idx) => (
                                            <div
                                                key={idx}
                                                className="landing-page-suggestions-item"
                                                onMouseDown={() => handleSelectCourt(item)}
                                            >
                                                {item}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="landing-page-suggestions-empty">No suggestions found</div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div
                            style={{ width: "12%", position: "relative" }}
                            className="landing-page-citation-bar-tab-field"
                            ref={yearRef}
                        >
                            <input
                                placeholder="Year"
                                value={ncYear}
                                onChange={handleCitationYearChange}
                                onFocus={() => {
                                    if (yearSuggestions.length > 0) setShowYearDropdown(true);
                                }}
                                autoComplete="off"
                            />

                            {/* Dropdown */}
                            {showYearDropdown && (
                                <div style={{ top: "40px", left: "0px" }} className="landing-page-suggestions-dropdown">
                                    {isLoadingYear ? (
                                        <div className="landing-page-suggestions-loading">Loading...</div>
                                    ) : yearSuggestions.length > 0 ? (
                                        yearSuggestions.map((item, idx) => (
                                            <div
                                                key={idx}
                                                className="landing-page-suggestions-item"
                                                onMouseDown={() => handleSelectYear(item)}
                                            >
                                                {item}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="landing-page-suggestions-empty">No suggestions found</div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div
                            style={{ width: "12%", position: "relative" }}
                            className="landing-page-citation-bar-tab-field"
                            ref={abbrRef}
                        >
                            <input
                                placeholder="NC-Abbr"
                                value={citationAbbr}
                                onChange={handleChange}
                                onFocus={() => {
                                    if (!selectedFromDropdown) {
                                        // Trigger API with empty prefix on focus
                                        setIsLoadingAbbr(true);
                                        fetchNeutralAbbrSuggestions("", ncYear, courtName)
                                            .then((data) => {
                                                setAbbrSuggestions(data);
                                                setShowAbbrDropdown(true);
                                            })
                                            .catch(console.error)
                                            .finally(() => setIsLoadingAbbr(false));
                                    }
                                }}
                                autoComplete="off"
                            />
                            {/* Dropdown */}
                            {showAbbrDropdown && (
                                <div style={{ top: "40px", left: "0px" }} className="landing-page-suggestions-dropdown">
                                    {isLoadingAbbr ? (
                                        <div className="landing-page-suggestions-loading">Loading...</div>
                                    ) : abbrSuggestions.length > 0 ? (
                                        abbrSuggestions.map((item, idx) => (
                                            <div
                                                key={idx}
                                                className="landing-page-suggestions-item"
                                                onMouseDown={() => handleSelectAbbr(item)}
                                            >
                                                {item}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="landing-page-suggestions-empty">Not found</div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div
                            style={{ width: "12%", position: "relative" }}
                            className="landing-page-citation-bar-tab-field"
                            ref={citationPageRef}
                        >
                            <input
                                placeholder="Page No."
                                value={citationPage}
                                onChange={handleCitationPageChange}
                                autoComplete="off"
                            />
                            {showCitationPageDropdown && (
                                <div className="landing-page-suggestions-dropdown">
                                    {isLoadingCitationPage ? (
                                        <div className="landing-page-suggestions-loading">Loading...</div>
                                    ) : citationPageSuggestions.length > 0 ? (
                                        citationPageSuggestions.map((item, idx) => (
                                            <div
                                                key={idx}
                                                className="landing-page-suggestions-item"
                                                onMouseDown={() => handleSelectCitationPage(item)}
                                            >
                                                {item}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="landing-page-suggestions-empty">No suggestions found</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )
            }
        </div>
    );
}
export default Citation;